import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getJobHistory,
  retryJob,
  deleteJob,
  JobHistoryJob,
  JobHistoryFilters,
} from '../utils/documentUpload/asyncProcessing';

const SOURCE_LABELS: Record<string, string> = {
  web: '🌐 Web',
  'web-retry': '🔄 Web Retry',
  telegram: '✈️ Telegram',
};

const STATUS_BADGE: Record<string, string> = {
  completed: 'badge bg-success',
  failed: 'badge bg-danger',
  processing: 'badge bg-warning text-dark',
};

const DOC_TYPE_BADGE: Record<string, string> = {
  'Fuel Receipt': 'badge bg-info text-dark',
  'Delivery Note': 'badge bg-primary',
};

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatSeconds(s?: number) {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

const JobHistoryPage: React.FC = () => {
  const { t } = useLanguage();

  const [filters, setFilters] = useState<JobHistoryFilters>({ days: 7 });
  const [pendingFilters, setPendingFilters] = useState<JobHistoryFilters>({ days: 7 });
  const [jobs, setJobs] = useState<JobHistoryJob[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0, processing: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHistory = useCallback(async (f: JobHistoryFilters) => {
    setLoading(true);
    setError('');
    try {
      const res = await getJobHistory(f);
      setJobs(res.jobs);
      setStats({ total: res.total, completed: res.completed, failed: res.failed, processing: res.processing });
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(filters);
  }, [filters, fetchHistory]);

  // Auto-refresh every 10s when there are processing jobs
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (stats.processing > 0) {
      autoRefreshRef.current = setInterval(() => fetchHistory(filters), 10000);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [stats.processing, filters, fetchHistory]);

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
  };

  const handleResetFilters = () => {
    const reset: JobHistoryFilters = { days: 7 };
    setPendingFilters(reset);
    setFilters(reset);
  };

  const handleRetry = async (job: JobHistoryJob) => {
    setActionMsg('');
    try {
      await retryJob(job.jobId);
      // Immediately refresh to get the authoritative DB state — the optimistic local update above was replaced with a real fetch
      // to prevent stale "processing" status from persisting after the job completes.
      await fetchHistory(filters);
      setActionMsg(`✅ "${job.fileName}" has been re-queued for processing.`);
    } catch (e: any) {
      setActionMsg(`❌ Retry failed: ${e.message}`);
    }
  };

  const handleDelete = async (job: JobHistoryJob) => {
    if (!window.confirm(`${t('confirm.delete.job')?.replace('{fileName}', job.fileName) || `Delete job for "${job.fileName}"?`}`)) return;
    setActionMsg('');
    try {
      await deleteJob(job.jobId);
      setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
      setActionMsg(`🗑️ Job for "${job.fileName}" deleted.`);
    } catch (e: any) {
      setActionMsg(`❌ Delete failed: ${e.message}`);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center">
              <h4 className="mb-0">{t('processing.history')}</h4>
              <button
                className="btn btn-sm btn-outline-secondary ms-auto"
                onClick={() => fetchHistory(filters)}
                disabled={loading}
              >
                {loading ? '⏳' : '🔄'} {t('refresh')}
              </button>
            </div>
            <div className="card-body">
              {/* Filter Bar */}
              <div className="row g-2 align-items-end mb-3">
                <div className="col-auto">
                  <label className="form-label small mb-1">{t('days')}</label>
                  <select
                    className="form-select form-select-sm"
                    value={pendingFilters.days ?? 7}
                    onChange={e => setPendingFilters(prev => ({ ...prev, days: Number(e.target.value) }))}
                  >
                    <option value={7}>7 {t('days')}</option>
                    <option value={14}>14 {t('days')}</option>
                    <option value={30}>30 {t('days')}</option>
                    <option value={0}>{t('all')}</option>
                  </select>
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">{t('document.type')}</label>
                  <select
                    className="form-select form-select-sm"
                    value={pendingFilters.documentType ?? ''}
                    onChange={e => setPendingFilters(prev => ({ ...prev, documentType: e.target.value || undefined }))}
                  >
                    <option value="">{t('all.types')}</option>
                    <option value="fuel_receipt">Fuel Receipt</option>
                    <option value="delivery_note">Delivery Note</option>
                  </select>
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">{t('source')}</label>
                  <select
                    className="form-select form-select-sm"
                    value={pendingFilters.source ?? ''}
                    onChange={e => setPendingFilters(prev => ({ ...prev, source: e.target.value || undefined }))}
                  >
                    <option value="">{t('all.sources')}</option>
                    <option value="web">Web</option>
                    <option value="web-retry">Web Retry</option>
                    <option value="telegram">Telegram</option>
                  </select>
                </div>
                <div className="col-auto">
                  <label className="form-label small mb-1">{t('status')}</label>
                  <select
                    className="form-select form-select-sm"
                    value={pendingFilters.status ?? ''}
                    onChange={e => setPendingFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
                  >
                    <option value="">{t('all.statuses')}</option>
                    <option value="completed">{t('completed')}</option>
                    <option value="failed">{t('failed')}</option>
                    <option value="processing">{t('processing')}</option>
                  </select>
                </div>
                <div className="col-auto">
                  <button className="btn btn-sm btn-primary me-1" onClick={handleApplyFilters}>
                    {t('apply')}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleResetFilters}>
                    {t('reset')}
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <div className="card text-center py-2">
                    <div className="fs-4 fw-bold">{stats.total}</div>
                    <div className="text-muted small">{t('total')}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card text-center py-2 border-success">
                    <div className="fs-4 fw-bold text-success">{stats.completed}</div>
                    <div className="text-muted small">{t('completed')}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card text-center py-2 border-danger">
                    <div className="fs-4 fw-bold text-danger">{stats.failed}</div>
                    <div className="text-muted small">{t('failed')}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card text-center py-2 border-warning">
                    <div className="fs-4 fw-bold text-warning">{stats.processing}</div>
                    <div className="text-muted small">{t('processing')}</div>
                  </div>
                </div>
              </div>

              {/* Action message */}
              {actionMsg && (
                <div className="alert alert-info py-2 mb-2">{actionMsg}</div>
              )}

              {/* Error */}
              {error && (
                <div className="alert alert-danger py-2 mb-2">{error}</div>
              )}

              {/* Table */}
              {loading && jobs.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div className="fs-1">📂</div>
                  <div>{t('no.jobs.found')}</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>{t('file.name')}</th>
                        <th>{t('document.type')}</th>
                        <th>{t('source')}</th>
                        <th>{t('status')}</th>
                        <th>{t('created.at')}</th>
                        <th>{t('completed.at')}</th>
                        <th>{t('processing.time')}</th>
                        <th>{t('error.reason')}</th>
                        <th>{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map(job => (
                        <tr key={job.jobId}>
                          <td className="text-break" style={{ maxWidth: 200 }}>{job.fileName}</td>
                          <td>
                            {job.documentType ? (
                              <span className={DOC_TYPE_BADGE[job.documentType] ?? 'badge bg-secondary'}>
                                {job.documentType}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {SOURCE_LABELS[job.source] ?? job.source}
                            </span>
                          </td>
                          <td>
                            <span className={STATUS_BADGE[job.status] ?? 'badge bg-secondary'}>
                              {job.status}
                            </span>
                            {job.status === 'processing' && (
                              <span className="ms-1 spinner-border spinner-border-sm text-warning" role="status" />
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDate(job.createdAt)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDate(job.completedAt)}</td>
                          <td>{formatSeconds(job.processingTime)}</td>
                          <td className="text-break" style={{ maxWidth: 200 }}>
                            {job.status === 'failed' ? (
                              <span className="text-danger small">{job.errorMessage || job.message || '—'}</span>
                            ) : (
                              <span className="text-muted small">{job.message || '—'}</span>
                            )}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {job.canRetry && (
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => handleRetry(job)}
                                title={t('retry')}
                              >
                                🔄 {t('retry')}
                              </button>
                            )}
                            {!job.canRetry && job.status === 'failed' && (
                              <span
                                className="badge bg-secondary me-1"
                                title={t('no.retry.available')}
                              >
                                {t('no.retry.available')}
                              </span>
                            )}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(job)}
                              title={t('delete')}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {stats.processing > 0 && (
                <div className="text-muted small text-end mt-1">
                  ⏱️ Auto-refreshing every 10s while jobs are processing…
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobHistoryPage;
